// OnboardingHelpers.jsx — guided tour, setup checklist, help center, help FAB.
//
// The firm wizard, intake forms and employee profile wizard already exist in
// the app; this file is purely additive — UX scaffolding around them.
//
//   <OnboardingHelpersProvider>          // wraps the app under AppShell
//     <AppShell>...</AppShell>
//     <OnboardingHost />                 // renders modals/overlays
//   </OnboardingHelpersProvider>
//
//   const { startTour, openHelp, openChecklist } = useOnboardingHelpers();
//
// Tour targets: AppShell nav buttons get data-tour="nav-key" attributes.
// Persistence: per-firm flags in localStorage keyed by firm.id so each
// firm sees the tour exactly once.

import React, {
  createContext, useContext, useState, useEffect, useMemo, useCallback,
} from 'react';
import {
  X, ChevronLeft, ChevronRight, Check, HelpCircle, BookOpen, Play, Rocket,
  Search, MessageCircle, ExternalLink, ArrowRight,
  ChevronDown, ChevronUp, CheckCircle2, Circle, Sparkles, Sprout, Trash2, FolderPlus,
} from 'lucide-react';
import { Card, Button, Modal, Badge } from '../ui';
import { useFirm } from '../../contexts/FirmContext';
import { useRoute } from '../../lib/router';
import { classNames } from '../../lib/formatters';
import { loadSampleData, wipeSampleData, seedStarterPack, markOnboardingStepSeen } from '../../lib/db';
import { useAuth } from '../../contexts/AuthContext';
import DocumentClassificationGuide from './DocumentClassificationGuide';
import ITOnboardingGuide from './ITOnboardingGuide';
import toast from '../../lib/toast';

/* =====================================================================
 *  Storage — scoped per firm so each firm sees the tour & checklist once.
 * ===================================================================== */

const lsKey = (firmId, name) => `lf_onb_${firmId || 'anon'}_${name}_v1`;
const safeJSON = (raw, fb) => { try { return raw ? JSON.parse(raw) : fb; } catch { return fb; } };

const Store = {
  getTour:   (firmId)    => safeJSON(localStorage.getItem(lsKey(firmId, 'tour')),    { completed: false, skipped: false }),
  setTour:   (firmId, v) => localStorage.setItem(lsKey(firmId, 'tour'), JSON.stringify(v)),
  getList:   (firmId)    => safeJSON(localStorage.getItem(lsKey(firmId, 'checklist')), null),
  setList:   (firmId, v) => localStorage.setItem(lsKey(firmId, 'checklist'), JSON.stringify(v)),
  getDismissed: (firmId) => safeJSON(localStorage.getItem(lsKey(firmId, 'dismissed')), false),
  setDismissed: (firmId, v) => localStorage.setItem(lsKey(firmId, 'dismissed'), JSON.stringify(v)),
  // Records that the user has completed the full checklist (8/8) AND
  // we've already shown the brief "You're all set up!" celebration.
  // Once true, the SetupChecklistWidget hides itself permanently for
  // this firm/browser. Stored separately from `dismissed` so we can
  // distinguish "user actively hid it before finishing" from "the
  // app auto-hid it after completion."
  getCompletedAck: (firmId)    => safeJSON(localStorage.getItem(lsKey(firmId, 'completed_ack')), false),
  setCompletedAck: (firmId, v) => localStorage.setItem(lsKey(firmId, 'completed_ack'), JSON.stringify(v)),
  getRecent: (firmId)    => safeJSON(localStorage.getItem(lsKey(firmId, 'help_recent')), []),
  setRecent: (firmId, v) => localStorage.setItem(lsKey(firmId, 'help_recent'), JSON.stringify(v)),
};

const DEFAULT_CHECKLIST = [
  { id: 'first_contact',  label: 'Add your first contact / client',  path: '/contacts',  est: '2 min' },
  { id: 'first_matter',   label: 'Open your first case file',         path: '/matters',   est: '3 min' },
  { id: 'first_document', label: 'Upload your first document',        path: '/documents', est: '1 min' },
  // New step pointing at the document-classification help article.
  // Sits next to "Upload your first document" so the user understands
  // the security model BEFORE they upload something sensitive under
  // the wrong tier. Resolves to the help-center article when clicked.
  { id: 'doc_classifications', label: 'Understand document classifications', path: null, est: '2 min', action: 'help', articleId: 'doc-classification' },
  { id: 'first_intake',   label: 'Publish an online intake form',     path: '/intake',    est: '2 min' },
  { id: 'first_time',     label: 'Log your first time entry',         path: '/time',      est: '1 min' },
  { id: 'first_invoice',  label: 'Send your first invoice',           path: '/billing',   est: '3 min' },
  { id: 'invite_team',    label: 'Invite a teammate',                  path: '/employees', est: '1 min' },
  { id: 'product_tour',   label: 'Take the product tour',              path: null,        est: '90 sec', action: 'tour' },
];

/* =====================================================================
 *  Context / Provider
 * ===================================================================== */

const OnboardingHelpersContext = createContext(null);
export const useOnboardingHelpers = () => {
  const ctx = useContext(OnboardingHelpersContext);
  if (!ctx) throw new Error('useOnboardingHelpers must be used inside <OnboardingHelpersProvider>');
  return ctx;
};

export function OnboardingHelpersProvider({ children }) {
  const { firm } = useFirm();
  const { userProfile } = useAuth();
  const firmId = firm?.id || null;

  // Tour seen-state is now per-USER, not per-firm. Source of truth:
  // public.users.tour_seen_at (migration 126). The local `tour` state is
  // still kept around so the legacy {completed, skipped} shape stays
  // compatible with the rest of the file, but we initialise it from the
  // user's profile flag rather than localStorage.
  const tourSeenServer = !!userProfile?.tour_seen_at;
  const [tour, setTour]           = useState(() => Store.getTour(firmId));
  const [checklist, setChecklist] = useState(() => Store.getList(firmId) || DEFAULT_CHECKLIST);
  const [dismissed, setDismissed] = useState(() => Store.getDismissed(firmId));

  const [tourActive, setTourActive] = useState(false);
  const [showHelp,   setShowHelp]   = useState(false);
  // `helpInitialArticle` lets callers of openHelp(id) deep-link to a
  // specific article. The HelpCenter modal seeds its own openId from
  // this when shown so users land directly on the article they asked
  // for (e.g. from the SetupChecklist "Understand document
  // classifications" step) instead of the article list.
  const [helpInitialArticle, setHelpInitialArticle] = useState(null);

  // Re-hydrate when the firm changes (e.g., super-admin impersonation switch)
  useEffect(() => {
    setTour(Store.getTour(firmId));
    setChecklist(Store.getList(firmId) || DEFAULT_CHECKLIST);
    setDismissed(Store.getDismissed(firmId));
  }, [firmId]);

  // Sync server's tour_seen_at into local tour state so all the
  // downstream checks (`tour.completed || tour.skipped`) read truthy
  // for users who have already done it on another device / session.
  useEffect(() => {
    if (tourSeenServer && !tour.completed && !tour.skipped) {
      setTour({ completed: true, skipped: false, finishedAt: userProfile.tour_seen_at });
    }
  }, [tourSeenServer, userProfile?.tour_seen_at, tour.completed, tour.skipped]);

  // Auto-start the tour once, right after a firm completes setup —
  // but ONLY if this user has never seen it (server flag is null).
  useEffect(() => {
    if (!firm || !firm.setup_completed) return;
    if (tour.completed || tour.skipped) return;
    if (tourSeenServer) return;
    const t = setTimeout(() => setTourActive(true), 1200);
    return () => clearTimeout(t);
  }, [firm, tour.completed, tour.skipped, tourSeenServer]);

  // persist
  useEffect(() => { if (firmId) Store.setTour(firmId, tour); }, [firmId, tour]);
  useEffect(() => { if (firmId) Store.setList(firmId, checklist); }, [firmId, checklist]);
  useEffect(() => { if (firmId) Store.setDismissed(firmId, dismissed); }, [firmId, dismissed]);

  const completeItem = useCallback((id) => {
    setChecklist((prev) => prev.map((it) => (it.id === id ? { ...it, done: true } : it)));
  }, []);

  const toggleItem = useCallback((id) => {
    setChecklist((prev) => prev.map((it) => (it.id === id ? { ...it, done: !it.done } : it)));
  }, []);

  const finishTour = useCallback(() => {
    setTourActive(false);
    setTour({ completed: true, skipped: false, finishedAt: new Date().toISOString() });
    completeItem('product_tour');
    // Persist to public.users.tour_seen_at so it sticks across logins / devices.
    markOnboardingStepSeen('tour').catch(() => { /* best-effort */ });
  }, [completeItem]);

  const skipTour = useCallback(() => {
    setTourActive(false);
    setTour({ completed: false, skipped: true, skippedAt: new Date().toISOString() });
    markOnboardingStepSeen('tour').catch(() => { /* best-effort */ });
  }, []);

  const value = useMemo(() => ({
    firm, checklist, tour, tourActive, showHelp, dismissed,
    startTour:       () => setTourActive(true),
    restartTour:     () => { setTour({ completed: false, skipped: false }); setTourActive(true); },
    finishTour, skipTour,
    openHelp:        (topicId) => {
      setShowHelp(true);
      setHelpInitialArticle(topicId || null);
      if (topicId && firmId) {
        const recent = [topicId, ...Store.getRecent(firmId).filter((t) => t !== topicId)].slice(0, 5);
        Store.setRecent(firmId, recent);
      }
    },
    closeHelp:       () => { setShowHelp(false); setHelpInitialArticle(null); },
    helpInitialArticle,
    completeItem, toggleItem,
    dismissChecklist:  () => setDismissed(true),
    restoreChecklist:  () => setDismissed(false),
    resetChecklist:    () => setChecklist(DEFAULT_CHECKLIST),
  }), [firm, checklist, tour, tourActive, showHelp, dismissed, firmId, completeItem, toggleItem, finishTour, skipTour, helpInitialArticle]);

  return <OnboardingHelpersContext.Provider value={value}>{children}</OnboardingHelpersContext.Provider>;
}

/* =====================================================================
 *  1.  Setup checklist widget (sits on the dashboard)
 * ===================================================================== */

export function SetupChecklistWidget() {
  const { firm, checklist, dismissed, toggleItem, dismissChecklist, startTour, openHelp } = useOnboardingHelpers();
  const [, navigate] = useRoute();
  const [collapsed, setCollapsed] = useState(false);
  const [sampleBusy, setSampleBusy] = useState(false);
  const [sampleLoaded, setSampleLoaded] = useState(false);

  // Auto-hide-on-completion state.
  //
  // Behavior:
  //   * As the user works through the checklist, the card stays
  //     visible (same as before).
  //   * The moment all 8 items are checked, the card swaps to the
  //     "You're all set up!" celebration state with a green check.
  //   * After CELEBRATION_MS the card auto-hides — and the hide
  //     persists across sessions via the `completed_ack` flag in
  //     localStorage.
  //   * Subsequent mounts where completed_ack=true skip rendering
  //     entirely. Refreshing or navigating away doesn't bring the
  //     card back.
  //   * If the user manually clicks "Hide this checklist" while
  //     mid-way (the existing dismiss flow), that's still honored.
  //
  // Why a delay instead of hiding instantly:
  //   The celebration is the user's reward for finishing the
  //   setup. Hiding instantly on the 8th tick robs them of that
  //   "I did it" moment. 4 seconds is long enough to read the
  //   message but short enough that it doesn't linger.
  const CELEBRATION_MS = 4000;
  const firmId = firm?.id || null;
  const [completedAck, setCompletedAck] = useState(() => Store.getCompletedAck(firmId));
  // Reset the local ack flag if the firm context changes (e.g. user
  // switches between firms in dev).
  useEffect(() => { setCompletedAck(Store.getCompletedAck(firmId)); }, [firmId]);

  const doneCount  = checklist.filter((c) => c.done).length;
  const totalCount = checklist.length;
  const isFinished = doneCount === totalCount;

  // When the checklist transitions to fully finished AND we haven't
  // already ack'd this completion (so on first arrival at 8/8),
  // start the celebration timer. After the timer fires, persist the
  // ack to localStorage and re-render — the next render returns
  // null and the card vanishes.
  useEffect(() => {
    if (!firmId || !isFinished || completedAck) return;
    const t = setTimeout(() => {
      Store.setCompletedAck(firmId, true);
      setCompletedAck(true);
    }, CELEBRATION_MS);
    return () => clearTimeout(t);
  }, [firmId, isFinished, completedAck]);

  // Visibility rules:
  //   - missing context / firm not set up / user dismissed → hide (existing)
  //   - finished AND ack'd → hide (auto-hide on completion)
  if (!firm || !firm.setup_completed || dismissed) return null;
  if (isFinished && completedAck) return null;

  const handleLoadSample = async () => {
    setSampleBusy(true);
    const { data, error } = await loadSampleData();
    setSampleBusy(false);
    if (error) { toast.error('Could not load sample data: ' + error.message); return; }
    setSampleLoaded(true);
    if (data?.status === 'already_loaded') {
      toast.success('Sample data is already in your account.');
    } else {
      toast.success(`Sample data loaded — explore Matters and Billing to see it.`);
    }
  };

  const handleWipeSample = async () => {
    if (!window.confirm('Remove all sample data? This deletes the example clients, matters, invoices and payments tagged as samples. Your real data is untouched.')) return;
    setSampleBusy(true);
    const { error } = await wipeSampleData();
    setSampleBusy(false);
    if (error) { toast.error('Could not wipe sample data: ' + error.message); return; }
    setSampleLoaded(false);
    toast.success('Sample data removed.');
  };

  const handleSeedFolders = async () => {
    const areas = firm?.practice_areas || [];
    if (areas.length === 0) {
      toast.error('Set your firm\'s practice areas in Settings first, then come back here.');
      return;
    }
    setSampleBusy(true);
    const { data, error } = await seedStarterPack(areas);
    setSampleBusy(false);
    if (error) { toast.error('Could not add folders: ' + error.message); return; }
    const added = data?.added || 0;
    if (added > 0) {
      toast.success(`Added ${added} document folder${added === 1 ? '' : 's'}. Find them on the Documents page.`);
    } else {
      toast.success('All suggested folders are already in your account.');
    }
  };

  // doneCount / totalCount / isFinished are computed up top now (for the
  // auto-hide-on-completion logic). Alias them to the names the rest of
  // this component already uses so the JSX below stays untouched.
  const done     = doneCount;
  const total    = totalCount;
  const finished = isFinished;
  const pct      = Math.round((done / total) * 100);

  const handleClick = (item) => {
    if (item.action === 'tour') return startTour();
    if (item.action === 'help') return openHelp(item.articleId);
    if (item.path) navigate(item.path);
  };

  return (
    <Card className="overflow-hidden">
      <button
        onClick={() => setCollapsed((c) => !c)}
        className="w-full flex items-center gap-3 p-4 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition text-left"
      >
        <div className={classNames(
          'flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center',
          finished ? 'bg-emerald-500 text-white' : 'bg-indigo-100 text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-300',
        )}>
          {finished ? <Check size={18} /> : <Rocket size={18} />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-slate-900 dark:text-white flex items-center gap-2">
            {finished ? "You're all set up!" : 'Get your firm running'}
            <Badge tone={finished ? 'green' : 'indigo'}>{done}/{total}</Badge>
          </div>
          <div className="mt-1.5 h-1.5 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-indigo-500 to-violet-500 transition-all"
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>
        {collapsed ? <ChevronDown size={16} className="text-slate-400" /> : <ChevronUp size={16} className="text-slate-400" />}
      </button>

      {!collapsed && (
        <div className="px-4 pb-4">
          <ul className="space-y-1">
            {checklist.map((item) => (
              <li
                key={item.id}
                className="flex items-center gap-3 group p-2 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800/50 transition"
              >
                <button
                  onClick={() => toggleItem(item.id)}
                  className="flex-shrink-0"
                  aria-label={item.done ? 'Mark not done' : 'Mark done'}
                >
                  {item.done
                    ? <CheckCircle2 size={18} className="text-emerald-500" />
                    : <Circle size={18} className="text-slate-300 dark:text-slate-600 group-hover:text-indigo-400" />}
                </button>
                <span className={classNames(
                  'flex-1 text-sm',
                  item.done
                    ? 'line-through text-slate-400 dark:text-slate-500'
                    : 'text-slate-700 dark:text-slate-200',
                )}>
                  {item.label}
                </span>
                <Badge tone="slate">{item.est}</Badge>
                {!item.done && (
                  <button
                    onClick={() => handleClick(item)}
                    className="opacity-0 group-hover:opacity-100 transition text-indigo-600 dark:text-indigo-300 hover:text-indigo-700 text-sm font-medium flex items-center gap-1"
                  >
                    Do it <ArrowRight size={14} />
                  </button>
                )}
              </li>
            ))}
          </ul>
          {/* Sample data + starter folders shortcuts */}
          <div className="mt-4 pt-3 border-t border-slate-100 dark:border-slate-800 space-y-2">
            {/* Sample data */}
            <div className="flex items-start gap-3 p-3 rounded-lg bg-gradient-to-br from-indigo-50 to-violet-50 dark:from-indigo-900/20 dark:to-violet-900/20 border border-indigo-100 dark:border-indigo-800/50">
              <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-indigo-600 flex items-center justify-center">
                <Sprout size={15} className="text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-900 dark:text-white">Want to explore with sample data?</p>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                  Loads two example clients, two matters and a sample invoice. Removable anytime.
                </p>
              </div>
              {sampleLoaded ? (
                <button
                  onClick={handleWipeSample}
                  disabled={sampleBusy}
                  className="flex-shrink-0 flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50"
                >
                  <Trash2 size={12} /> Remove
                </button>
              ) : (
                <button
                  onClick={handleLoadSample}
                  disabled={sampleBusy}
                  className="flex-shrink-0 px-3 py-1.5 text-xs font-medium rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white disabled:opacity-50"
                >
                  {sampleBusy ? 'Loading…' : 'Load'}
                </button>
              )}
            </div>

            {/* Starter folder pack — for firms that signed up before migration 118 */}
            <div className="flex items-start gap-3 p-3 rounded-lg bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/15 dark:to-teal-900/15 border border-emerald-100 dark:border-emerald-800/50">
              <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-emerald-600 flex items-center justify-center">
                <FolderPlus size={15} className="text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-900 dark:text-white">Add suggested document folders</p>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                  Pre-create folders matched to your practice areas — Pleadings, Discovery, Wills, Title Documents, etc.
                </p>
              </div>
              <button
                onClick={handleSeedFolders}
                disabled={sampleBusy}
                className="flex-shrink-0 px-3 py-1.5 text-xs font-medium rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50"
              >
                {sampleBusy ? 'Adding…' : 'Add folders'}
              </button>
            </div>
          </div>

          <div className="mt-3 flex items-center justify-between">
            <button
              onClick={dismissChecklist}
              className="text-xs text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
            >
              Hide this checklist
            </button>
            {finished && (
              <span className="text-xs text-emerald-600 dark:text-emerald-400 font-medium flex items-center gap-1">
                <Sparkles size={12} /> Everything set — congratulations!
              </span>
            )}
          </div>
        </div>
      )}
    </Card>
  );
}

/* =====================================================================
 *  2.  Guided product tour
 *    - looks for `data-tour="<key>"` elements in the DOM
 *    - cuts a spotlight out of a dark overlay over the target
 *    - if the target isn't found (mobile, hidden, route change),
 *      gracefully falls back to centred bubble
 * ===================================================================== */

export const DEFAULT_TOUR_STEPS = [
  { key: 'nav-dashboard',  title: 'Your dashboard',
    body: "What needs your attention today — open matters, deadlines, KPIs, recent activity." },
  { key: 'nav-matters',    title: 'Matters / case files',
    body: 'Every case lives here with its tasks, documents, deadlines, time entries, notes and ledger in one place.' },
  { key: 'nav-contacts',   title: 'Contacts & clients',
    body: 'Everyone you represent or work with. Click any contact to see the matters and intake submissions tied to them.' },
  { key: 'nav-time',       title: 'Time tracking',
    body: 'Log billable time fast. Entries flow straight into invoices on the billing tab.' },
  { key: 'nav-billing',    title: 'Billing',
    body: 'Turn time entries and expenses into invoices, send them, and reconcile payments.' },
  { key: 'nav-trust',      title: 'Client account',
    body: 'IOLTA-style trust ledger with three-way reconciliation. Only visible on Pro and above.' },
  { key: 'nav-intake',     title: 'Online intake',
    body: 'Build hosted intake forms, share the link with prospects, and run conflict checks automatically.' },
  { key: 'nav-help',       title: 'Help, anytime',
    body: 'Click this any time — search the docs, watch walkthroughs, restart this tour. You\'ve got this.' },
];

export function GuidedTour({ steps = DEFAULT_TOUR_STEPS }) {
  const { tourActive, finishTour, skipTour } = useOnboardingHelpers();
  const [idx, setIdx] = useState(0);
  const [rect, setRect] = useState(null);

  useEffect(() => { if (tourActive) setIdx(0); }, [tourActive]);

  const targetSel = `[data-tour="${steps[idx]?.key}"]`;

  useEffect(() => {
    if (!tourActive) return;
    const recalc = () => {
      const el = document.querySelector(targetSel);
      if (el) {
        const r = el.getBoundingClientRect();
        setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
        el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
      } else {
        setRect(null);
      }
    };
    recalc();
    const onResize = () => recalc();
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onResize, true);
    const interval = setInterval(recalc, 400);  // catch route/layout transitions
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onResize, true);
      clearInterval(interval);
    };
  }, [tourActive, targetSel]);

  if (!tourActive) return null;

  const total = steps.length;
  const step  = steps[idx];
  const next  = () => (idx < total - 1 ? setIdx(idx + 1) : finishTour());
  const prev  = () => setIdx(Math.max(0, idx - 1));

  let bubbleStyle = { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
  if (rect) {
    const bubbleW = 340;
    const bubbleH = 200;
    // Prefer to the right of the sidebar item; otherwise below
    if (rect.left + rect.width + bubbleW + 32 < window.innerWidth) {
      bubbleStyle = { top: Math.max(16, rect.top), left: rect.left + rect.width + 16 };
    } else if (rect.top + rect.height + bubbleH < window.innerHeight) {
      bubbleStyle = { top: rect.top + rect.height + 12, left: Math.max(16, Math.min(window.innerWidth - bubbleW - 16, rect.left)) };
    } else {
      bubbleStyle = { top: Math.max(16, rect.top - bubbleH - 12), left: Math.max(16, Math.min(window.innerWidth - bubbleW - 16, rect.left)) };
    }
  }

  return (
    <div className="fixed inset-0 z-[200] pointer-events-none">
      <svg className="absolute inset-0 w-full h-full pointer-events-auto" onClick={next} aria-label="Tour overlay — click to advance">
        <defs>
          <mask id="lf-tour-mask">
            <rect width="100%" height="100%" fill="white" />
            {rect && (
              <rect
                x={rect.left - 6} y={rect.top - 6}
                width={rect.width + 12} height={rect.height + 12}
                rx="10" fill="black"
              />
            )}
          </mask>
        </defs>
        <rect width="100%" height="100%" fill="rgba(15,23,42,0.7)" mask="url(#lf-tour-mask)" />
        {rect && (
          <rect
            x={rect.left - 6} y={rect.top - 6}
            width={rect.width + 12} height={rect.height + 12}
            rx="10" fill="none" stroke="#818cf8" strokeWidth="2"
            className="animate-pulse"
          />
        )}
      </svg>

      <div
        className="absolute pointer-events-auto w-[340px] rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 shadow-2xl p-4"
        style={bubbleStyle}
      >
        <div className="flex items-center justify-between mb-2">
          <Badge tone="indigo">Step {idx + 1} of {total}</Badge>
          <button onClick={skipTour} className="text-xs text-slate-400 hover:text-slate-600 dark:hover:text-slate-300">Skip tour</button>
        </div>
        <div className="font-semibold text-slate-900 dark:text-white mb-1">{step.title}</div>
        <div className="text-sm text-slate-600 dark:text-slate-300">{step.body}</div>
        <div className="flex items-center justify-between mt-4">
          <button
            onClick={prev}
            disabled={idx === 0}
            className="text-sm text-slate-500 disabled:opacity-30 flex items-center gap-1 dark:text-slate-400"
          >
            <ChevronLeft size={14} /> Back
          </button>
          <Button onClick={next} variant="primary">
            {idx === total - 1 ? <>Finish <Check size={14} /></> : <>Next <ChevronRight size={14} /></>}
          </Button>
        </div>
      </div>
    </div>
  );
}

/* =====================================================================
 *  3.  Help center — searchable knowledge base
 * ===================================================================== */

export const HELP_ARTICLES = [
  {
    id: 'add-first-contact', category: 'Getting started',
    title: 'How do I add my first contact?',
    body: `1. Open **Contacts** from the sidebar.
2. Click **+ New contact** at the top right.
3. Fill in name, email and any other detail you have — only name is required.
4. **Save**. Your contact is now in the list, ready to attach matters to.

To let prospects fill in their own info, publish an **intake form** from the *Intake* tab and share the link.`
  },
  {
    id: 'open-matter', category: 'Getting started',
    title: 'How do I open a new case file?',
    body: `1. Open **Case Files** from the sidebar.
2. Click **+ New matter**.
3. Pick the **client** (or create one inline), give the matter a short title and pick a practice area.
4. Optionally set assigned lawyers, priority and a target close date.
5. **Save**. The matter opens with tabs for tasks, documents, time entries, deadlines, notes and a timeline.`
  },
  {
    id: 'intake-form', category: 'Client experience',
    title: 'Publish an online intake form',
    body: `1. Go to **Intake** in the sidebar.
2. Click **New form**, name it (e.g. "General intake") and pick which fields prospects should fill in.
3. **Publish** — you'll get a public URL like \`yourfirm.com/intake/abc\` you can email, text, or embed.
4. Submissions arrive under **Intake → Submissions** and feed into the **Conflict checker** before you turn them into matters.`
  },
  {
    id: 'conflict-check', category: 'Client experience',
    title: 'Run a conflict-of-interest check',
    body: `Open any intake submission (or **Conflict** in the sidebar). The system searches existing contacts, matters and adverse parties for name and email matches and shows the strength of each hit.
Always run a conflict check **before** sending an engagement letter. The check is on Pro and above.`
  },
  {
    id: 'upload-doc', category: 'Documents',
    title: 'Upload and tag a document',
    body: `Drag any file onto a matter to attach it — or open **Documents** and click upload. Uploading a file with the same name creates a new **version**, with previous versions accessible from the document detail panel.
Tag a document with a **type** (Contract, Brief, Court Filing, etc.) so search and filters work well.`
  },
  {
    // Rich-component article. `body` is kept short for search hits and
    // the article-list preview snippet; the actual rendered body comes
    // from the `Component` field which shows a colour-coded table of
    // the five tiers, their enforcement model, and recommended usage.
    id: 'doc-classification', category: 'Documents',
    title: 'What do the document classification levels mean?',
    body: `Five tiers from Public to Strictly Confidential — each enforced server-side by the database, not just by the UI. Controls who in your firm (and which clients via the portal) can see, download or edit a given document.`,
    Component: () => <DocumentClassificationGuide variant="full" />,
  },
  {
    id: 'time-billing', category: 'Billing',
    title: 'Log time and turn it into an invoice',
    body: `Open any matter and click the **Time** tab. Enter the date, hours and a description, then save.
At the end of the period go to **Billing → New invoice**, pick the client, and tick which time entries to include. Trust account transactions stay separate under **Client Account** to keep funds traceable.`
  },
  {
    id: 'trust-account', category: 'Billing',
    title: 'Operate a client trust / IOLTA account',
    body: `**Client Account** (Pro and above) gives each client a separate balance with full deposit / withdrawal history.
At month-end, run **Three-way reconciliation** to match: bank statement ↔ ledger total ↔ sum of client balances. The system flags any mismatch.`
  },
  {
    id: 'invite-team', category: 'Team',
    title: 'Invite a teammate',
    body: `Go to **Employees → Add employee** (or **Settings → Team → Invite & grant access**). Type their name and email, pick a role and **Save**.

Available roles:
- **Admin / Partner** — full firm access
- **Attorney / Associate / Paralegal / Clerk** — assigned matters only
- **IT** — settings + team management + documents (no case / billing access)

They'll receive a sign-in invite by email. To change someone's permissions later, edit their role on the same screen.`
  },

  // ─── IT category ─────────────────────────────────────────────────
  // The IT_ONBOARDING_GUIDE.docx in public/ is the printable / offline
  // version. The articles below give the IT person searchable in-app
  // access to the same content. The "full guide" article uses a rich
  // <Component> that mirrors every section of the DOCX and includes a
  // download button for offline / printing use.

  {
    id: 'it-onboarding-full',
    category: 'IT',
    title: 'IT onboarding guide (complete walkthrough)',
    body: `Step-by-step playbook for onboarding every new employee — attorneys, paralegals, clerks, accountants, and admin staff — onto your firm's DocketFlow account. ~10–15 minutes per person. Covers roles, invites, first login, security baseline, mobile setup, and offboarding. A Word-doc version is downloadable for printing or offline reference.`,
    Component: ITOnboardingGuide,
  },
  {
    id: 'it-invite-new-employee',
    category: 'IT',
    title: 'IT: Invite a new employee (5-minute path)',
    body: `1. Go to **Settings → Team** in DocketFlow.
2. Click **Invite Team Member**.
3. Fill in the employee's first name, last name, email, mobile (10-digit Ghana format), and pick the right role:
   - **Admin / Partner** — full firm access
   - **Attorney / Associate / Paralegal / Clerk** — assigned matters only
   - **IT** — settings + team management + documents (no case / billing access)
4. Click **Send invite**. The employee receives an email link.
5. Tell them to **check spam** if they don't see it within 5 minutes.
6. The invite link expires after **7 days** — use **Resend invite** on their row if needed.

For the complete onboarding playbook including first-login walkthrough, role-specific configuration, security baseline, and mobile setup, open the **IT onboarding guide** article in this Help Center.`
  },
  {
    id: 'it-choose-role',
    category: 'IT',
    title: 'IT: Which role should I assign?',
    body: `Pick by job description, not by seniority:

- **Admin** — senior partner or office manager running everything. Sees all matters firm-wide.
- **Partner** — equity / senior partners. Same access as Admin minus role management.
- **Attorney** — practising attorneys. Only sees matters they're assigned to.
- **Associate** — junior attorneys still under supervision. Same as Attorney.
- **Paralegal** — paralegals, legal assistants. Only assigned matters; cannot create invoices.
- **Clerk** — filing clerks, secretaries. Only assigned matters; minimal billing access.
- **IT** — you. Settings + team management + documents. **Cannot see matters, billing, or contacts** — intentional, for client confidentiality.

**Confidential matters** (matters marked with the 🔒 badge by Admin/Partner) are visible only to leaders, the responsible attorney, and people explicitly added to the matter team. For everyone else they're hidden entirely, even when they technically have the right role.

**Tip:** if you don't yet know an employee's role on day one, invite them as **Clerk**. You can promote them later from Settings → Team without re-sending the invite.`
  },
  {
    id: 'it-offboard-employee',
    category: 'IT',
    title: 'IT: Offboarding — when an employee leaves',
    body: `**Day of departure (do all of this before they leave the building):**

1. **Settings → Team** → click their row → **Suspend account**. They lose access immediately. Auth session stays alive (so unsuspending is a one-click restore).
2. **Reassign open matters**: Matter → Edit → change Responsible attorney.
3. **Reassign open tasks**: Tasks page → filter by assignee → bulk reassign.
4. If they were on **confidential matter teams**, remove them from Matter → Team.

**Within 30 days:**

5. **Settings → Team** → suspended row → **Delete account**. This is a *soft delete* — their work product (matter notes, time entries, message history) stays intact, but their login is removed permanently.
6. **Audit**: Reports → Activity log for the last 90 days of their activity. Save a copy if firm policy requires.

**Do NOT hard-delete on the same day.** Wait at least 7 days in case they need to be queried for handover. Suspension achieves the access-revocation goal without losing data.`
  },
  {
    id: 'it-mobile-setup',
    category: 'IT',
    title: 'IT: Help an employee install DocketFlow on their phone',
    body: `DocketFlow runs in the phone browser — there's no separate app to install. To get an app-like icon and push notifications:

**iPhone / iPad:**

1. Open DocketFlow in **Safari** (must be Safari — not Chrome).
2. Tap the **Share** button (square with arrow).
3. Scroll and tap **Add to Home Screen**.
4. Tap **Add**. A DocketFlow icon appears on the home screen.

**Android:**

1. Open DocketFlow in **Chrome**.
2. Tap the three-dot menu (top-right).
3. Tap **Install app** or **Add to Home screen**.
4. Confirm. The icon installs to the home screen and app drawer.

Once installed, push notifications for @-mentions, matter updates, and deadline reminders can be enabled in **Settings → Notifications**.`
  },
  {
    id: 'it-remove-sample-data',
    category: 'IT',
    title: 'IT: Remove sample / example data',
    body: `If your firm loaded sample data during onboarding (the two example clients, two matters, three tasks, sample invoice, and sample payment that ship under "SAMPLE-001" etc.), you can clean them up permanently when you're ready to use DocketFlow with only real records.

**How to remove sample data:**

1. Open **Settings** from the sidebar.
2. Click the **Data** tab (only visible to Admins / Partners).
3. The page shows a count of any sample records still in your firm, broken down by type (contacts, case files, tasks, invoices, payments).
4. Click **Remove all sample data**. Confirm the prompt.
5. Your real records are untouched — only rows tagged as samples are removed.

**Safe to run anytime.** The removal is permanent for those specific tagged rows but cannot affect any real client, matter, task, invoice, or payment you've created. The system identifies sample records by a database flag set when they were originally seeded — not by name or appearance.

**If the Data tab shows "No sample data found"**, either you never loaded sample data or you've already removed it. Nothing to clean up.

To **load** sample data (e.g. for training a new IT hire), use the onboarding checklist on the Dashboard. The Help Center → IT category has the full IT onboarding guide.`
  },
  {
    id: 'it-troubleshooting',
    category: 'IT',
    title: 'IT: Common errors and what to try first',
    body: `**New employee never got the invite email** → check their spam folder. Then Settings → Team → Resend invite.

**Invite link shows "verifying…" forever** → the link expired or was opened in a different browser than the email. Resend, then open it in the same browser as the email.

**"No other team members found" in Direct Messages** → sign out and sign back in. If still empty, contact DocketFlow support.

**"New row violates row-level security policy"** on any action → sign out *fully* (not just refresh) and sign back in. Report to support with a screenshot if it persists.

**Employee can't see a matter they should see** → check Matter → Team. The responsible attorney needs to add them. Admins / Partners see everything automatically.

**Employee can't change their password** → Profile → Change password. If that fails, Admin can trigger a reset from Settings → Team → their row.

**Mobile site looks broken / cards too big** → hard-refresh (close all tabs, reopen). Most fixes ship every Friday; cached old versions are the usual culprit.

**Storage full when uploading** → Settings → Team → Storage section shows the usage. Click **Buy more storage** for upgrade tiers, or delete some existing documents to free space.`
  },
  {
    id: 'restart-tour', category: 'App basics',
    title: 'Re-run the product tour',
    body: `Click the **?** button (bottom-right) → **Replay product tour**. It highlights each section of the app in turn and takes about 90 seconds.`
  },
  {
    id: 'security', category: 'Security',
    title: 'How is my firm\'s data stored?',
    body: `All data is encrypted in transit (TLS 1.2+) and at rest (AES-256). Row-Level-Security policies in the database make sure firms can only ever see their own data — no cross-firm leakage is possible.
Backups run daily and are retained for 30 days. You can export your full dataset any time from **Settings → Data**.`
  },

  // ─── 5 new articles for common gaps ──────────────────────────────
  {
    id: 'setup-e2e-encryption', category: 'Security',
    title: 'Set up end-to-end encryption for sensitive channels',
    body: `End-to-end encryption (E2EE) lets your firm encrypt messages in a specific Team Chat channel so that even DocketFlow staff can't read them — only people in your firm who know the firm passphrase can decrypt.

**Who should turn this on:**
Firms handling high-stakes matters (M&A, internal investigations, regulatory inquiries, whistleblower correspondence) where you want a guarantee that no third party — including us — can read the messages even with database access.

**How to enable (one-time, firm-wide):**

1. Open **Settings → Security**.
2. Find the **End-to-end encryption** section. Only firm Admins / Partners can run this step.
3. Click **Set up encryption** and enter a strong passphrase (minimum 12 characters; use a phrase, not a single word — e.g. "Akosua-survey-2026-mediation").
4. Save. The passphrase is now active firm-wide.

**Mark a specific channel as encrypted:**

1. Open the channel in **Messages → Team chat**.
2. Click the lock icon next to the channel name.
3. Confirm. From now on, every new message in this channel is encrypted before it leaves the browser.

**How teammates unlock it:**

When they open an encrypted channel, they'll see "🔒 Encrypted — unlock encryption in Settings to read." They click **Settings → Security → Unlock**, enter the firm passphrase, and the messages decrypt in their browser.

**Important:**
- Share the passphrase only through secure channels (in person, signed encrypted email — **never** SMS or unencrypted email).
- If everyone forgets the passphrase, the encrypted messages are unrecoverable. The unencrypted ones are unaffected.
- Encryption applies to message bodies only. File attachments and metadata (who sent what, when) remain accessible via the regular controls.`
  },

  {
    id: 'trust-account-deep-dive', category: 'Billing',
    title: 'How the client trust / IOLTA account works',
    body: `A trust account is where client money sits BEFORE it becomes your firm's revenue. Ghana Bar rules (and IOLTA rules in most jurisdictions) require strict separation between operating funds and client funds. DocketFlow handles the ledger for you.

**The three-account model:**

- **Operating account** (your firm's bank account) — fees you've earned, expenses you pay out
- **Trust account** (separate bank account at your bank) — money clients have given you that isn't yet earned (retainers, settlement proceeds in transit, advance costs)
- **Client ledger** (in DocketFlow) — per-client subledger showing how much of the trust account belongs to each client

**Typical lifecycle:**

1. Client pays you a GH₵ 5,000 retainer for a property matter.
2. You deposit it in your trust bank account.
3. In DocketFlow: **Billing → Trust → New trust transaction → Deposit GH₵ 5,000 for Akosua Owusu**.
4. The client's trust ledger now shows GH₵ 5,000.
5. After a month of work, you invoice the client GH₵ 1,200 for time + survey costs.
6. **Billing → Invoice → Pay from trust** transfers GH₵ 1,200 from the client's trust ledger to your operating account.
7. The client's trust ledger drops to GH₵ 3,800; your firm has earned GH₵ 1,200.

**Three-way reconciliation (monthly):**

Open **Billing → Trust → Reconciliation**. The system checks three numbers:

- The trust bank account statement balance
- The total in DocketFlow's trust ledger
- The sum of all client trust balances

All three must match. If they don't, the system flags the discrepancy. **Run this every month** — Ghana Bar disciplinary procedures specifically check for trust account mismanagement.

**What you must NEVER do:**
- Move trust funds to operating before invoicing the work (that's commingling — a sanctionable offense)
- Pay another client's bill from a different client's trust funds
- Bounce checks out of the trust account (a separate offense even if you cover it later)`
  },

  {
    id: 'send-first-invoice', category: 'Billing',
    title: 'Send your first invoice to a client',
    body: `The full workflow from "I've done work" to "Client received the bill":

1. **Log the time** as you work (or backdate later): open the matter → **Time** tab → **Log time**. Enter date, hours, description, and rate. Save.
2. **Open Billing → New invoice**. Pick the client and matter.
3. The system pre-selects all unbilled time entries and unbilled expenses for that matter. Tick the ones you want on this invoice (you can split a busy month into two invoices if you prefer).
4. Set the issue date, due date (typically issue + 14 or 30 days), and any tax/discount.
5. **Add notes** that appear on the invoice — payment instructions, thank-you message, or reference numbers.
6. **Save as draft** to review, or **Send** to email it to the client immediately.

**What the client sees:**

The client receives an email with a PDF invoice attached and a link to view it in their portal. From the portal they can see line items, payment history, and pay online (if you've configured Paystack for client payments).

**Recording a payment:**

When the client pays:
- **Bank transfer / mobile money**: open the invoice → **Record payment** → enter amount, date, method, reference. The invoice marks as Partial or Paid automatically.
- **Card via the portal**: payment records itself — you'll see it appear on the invoice within seconds of the client paying.
- **From trust**: see the trust account article — different flow that draws from the client's trust ledger.

**Watch the **Billing dashboard** for outstanding invoices and aged receivables; the system flags overdue invoices in red automatically.`
  },

  {
    id: 'run-conflict-check', category: 'Client experience',
    title: 'Run a conflict-of-interest check before signing a new client',
    body: `A conflict check protects your firm from representing two clients with opposing interests — which can lead to disbarment, not just lost work. Always run one **before** you send an engagement letter.

**Quick check from an intake submission:**

1. Open **Intake → Submissions**.
2. Click the submission. The Conflict Check section is at the top.
3. The system searches your existing contacts, matters, and adverse parties for name and email matches.
4. Each hit gets a strength score: **Exact match** (red — investigate), **Close match** (amber — check carefully), **Partial match** (slate — likely false positive).

**Manual check (no intake submission needed):**

1. Open **Conflict** in the sidebar.
2. Type the prospect's name and any related parties (spouse, business partner, opposing party they've named).
3. Click **Run check**. Results appear with the same scoring.

**What to do with a hit:**

- **Exact match on the same person** → call them or refer out. You can't represent the same person on two adverse matters; you can sometimes represent them on unrelated matters if both clients waive (rare in Ghana).
- **Match on opposing party** → automatic disqualification on this matter. Refer out.
- **Match on a witness or expert** → usually not disqualifying, but document it.

**What conflict-check covers:**

The check searches across:
- All contacts (clients + prospects + opposing counsel + witnesses + experts)
- All matter titles and descriptions
- Adverse-party fields on every matter

It does NOT search file contents (that would require document OCR + intelligent matching — on the roadmap). If you've taken on a representation that's only mentioned in a single PDF, the check won't catch it — keep your contact records up to date.

**Document your check:**

The system logs every conflict check you run with a timestamp. If a regulatory question ever comes up, you can show you ran a check on date X and what it returned. **Conflict check is on Pro plan and above.**`
  },

  {
    id: 'client-portal-access', category: 'Client experience',
    title: 'How clients access the portal (and what they see)',
    body: `The client portal is a separate site at \`portal.docketflowapp.com\` (or your branded domain). Each of your clients gets their own login.

**Inviting a client:**

1. Open the client's contact record.
2. Click **Invite to portal**. The system sends an email with a one-time signup link.
3. The client clicks the link, sets a password, and lands on their portal dashboard.

**What the client sees:**

- Their matter(s) — title, status, key dates only (NOT the firm's internal notes, time entries, or any confidential matters)
- Documents you've shared with them specifically (each document has a "Visible to client" toggle — off by default)
- Invoices addressed to them — paid, partial, outstanding amounts and aging
- A secure messaging thread with your firm (whatever attorney you've assigned to handle their portal conversation)
- The ability to upload documents to your firm (e.g. when you ask for a signed engagement letter)
- The ability to make online payments via Paystack (if configured)

**What the client does NOT see:**

- Other clients' data (RLS enforces strict tenant isolation)
- Time entries, internal notes, attorney comments
- Documents you haven't explicitly shared with them
- Matters marked Confidential by your firm (unless they're directly involved in that matter)
- Your firm's internal team chat or any messaging that isn't on their thread

**Resetting a client's access:**

If a client forgets their password, they click **Forgot password** on the portal sign-in. If their email account is compromised, open their contact record → **Revoke portal access** → re-invite from a fresh email later.

**Multiple users at a client (e.g. a corporate client):**

Each authorized contact at the client organization can have their own portal login — invite each one separately from their contact record. They see the same matters but log in as themselves.

**Important:**
The portal is the most public-facing part of DocketFlow. Anything visible there should be polished and client-ready. Review documents and invoices before flipping the "Visible to client" toggle.`
  },
];

export function HelpCenter() {
  const { firm, showHelp, closeHelp, restartTour, helpInitialArticle } = useOnboardingHelpers();
  const firmId = firm?.id || null;
  const [q, setQ] = useState('');
  const [openId, setOpenId] = useState(null);

  // When openHelp(topicId) is invoked elsewhere in the app (the
  // SetupChecklist "Understand document classifications" step,
  // contextual "?" buttons, etc.), the HelpCenter modal opens AND
  // the context now exposes `helpInitialArticle`. Use it to seed our
  // openId so the user lands directly on the article they asked for.
  // Sync each time it changes so a second openHelp(otherId) call
  // while the modal is still mounted still navigates correctly.
  useEffect(() => {
    if (showHelp && helpInitialArticle) setOpenId(helpInitialArticle);
    if (!showHelp) setOpenId(null);
  }, [showHelp, helpInitialArticle]);
  const recent = firmId ? Store.getRecent(firmId) : [];

  const results = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return HELP_ARTICLES;
    return HELP_ARTICLES.filter((a) =>
      a.title.toLowerCase().includes(needle) ||
      a.body.toLowerCase().includes(needle) ||
      a.category.toLowerCase().includes(needle)
    );
  }, [q]);

  const grouped = useMemo(() => {
    const map = {};
    results.forEach((a) => { (map[a.category] ||= []).push(a); });
    return map;
  }, [results]);

  return (
    <Modal open={showHelp} onClose={closeHelp} size="xl" title="Help center">
      <div className="grid grid-cols-1 md:grid-cols-3 h-[60vh] -mx-6 -my-4">
        {/* left rail */}
        <div className="md:col-span-1 border-r border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50 p-5 overflow-y-auto">
          <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">Search how-tos or jump straight into a guided action.</p>

          <div className="relative mb-3">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search the docs…"
              className="w-full pl-9 pr-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm text-slate-900 dark:text-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 dark:focus:ring-indigo-500/30 outline-none"
            />
          </div>

          <div className="text-[10px] uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-2 font-semibold">Quick actions</div>
          <div className="space-y-1 mb-5">
            <button
              onClick={() => { closeHelp(); restartTour(); }}
              className="w-full flex items-center gap-2 p-2 rounded-lg hover:bg-white dark:hover:bg-slate-800 text-sm text-slate-700 dark:text-slate-200"
            >
              <Play size={14} className="text-indigo-600 dark:text-indigo-400" /> Replay product tour
            </button>
            <a
              href="mailto:support@docketflowapp.com"
              className="w-full flex items-center gap-2 p-2 rounded-lg hover:bg-white dark:hover:bg-slate-800 text-sm text-slate-700 dark:text-slate-200"
            >
              <MessageCircle size={14} className="text-indigo-600 dark:text-indigo-400" /> Email support
            </a>
          </div>

          {recent.length > 0 && (
            <>
              <div className="text-[10px] uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-2 font-semibold">Recently viewed</div>
              <div className="space-y-1 mb-5">
                {recent.map((id) => {
                  const a = HELP_ARTICLES.find((x) => x.id === id);
                  if (!a) return null;
                  return (
                    <button
                      key={id}
                      onClick={() => setOpenId(id)}
                      className="block w-full text-left text-sm text-slate-700 dark:text-slate-200 hover:text-indigo-700 dark:hover:text-indigo-300 truncate"
                    >
                      {a.title}
                    </button>
                  );
                })}
              </div>
            </>
          )}

          <div className="text-[10px] uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-2 font-semibold">Categories</div>
          <div className="space-y-1">
            {Array.from(new Set(HELP_ARTICLES.map((a) => a.category))).map((cat) => (
              <button
                key={cat}
                onClick={() => setQ(cat)}
                className="block w-full text-left text-sm text-slate-700 dark:text-slate-200 hover:text-indigo-700 dark:hover:text-indigo-300"
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        {/* right content */}
        <div className="md:col-span-2 overflow-y-auto p-7">
          {openId ? (
            <ArticleView
              article={HELP_ARTICLES.find((a) => a.id === openId)}
              onBack={() => setOpenId(null)}
            />
          ) : (
            <>
              <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-1">
                {q ? `Results for "${q}"` : 'How can we help?'}
              </h2>
              <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">
                {results.length} article{results.length === 1 ? '' : 's'}
              </p>

              {Object.keys(grouped).length === 0 ? (
                <div className="text-center py-16 text-slate-500 dark:text-slate-400">
                  <Search size={28} className="mx-auto mb-2 text-slate-300 dark:text-slate-600" />
                  Nothing matched. Try a different keyword or{' '}
                  <a className="text-indigo-600 dark:text-indigo-400" href="mailto:support@docketflowapp.com">email support</a>.
                </div>
              ) : Object.entries(grouped).map(([cat, items]) => (
                <div key={cat} className="mb-7">
                  <div className="text-[10px] uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-2 font-semibold">{cat}</div>
                  <div className="space-y-2">
                    {items.map((a) => (
                      <button
                        key={a.id}
                        onClick={() => setOpenId(a.id)}
                        className="block w-full text-left p-3 rounded-xl border border-slate-200 dark:border-slate-700 hover:border-indigo-300 dark:hover:border-indigo-600 hover:shadow-sm transition group bg-white dark:bg-slate-900"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <div className="font-semibold text-slate-900 dark:text-white group-hover:text-indigo-700 dark:group-hover:text-indigo-300">
                              {a.title}
                            </div>
                            <div className="text-sm text-slate-500 dark:text-slate-400 mt-0.5 line-clamp-2">
                              {a.body.split('\n')[0].replace(/[*_#]/g, '')}
                            </div>
                          </div>
                          <ArrowRight size={14} className="text-slate-300 dark:text-slate-600 group-hover:text-indigo-500 flex-shrink-0 mt-1" />
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      </div>
    </Modal>
  );
}

function ArticleView({ article, onBack }) {
  if (!article) return null;
  const lines = (article.body || '').split('\n');
  // Some articles need richer content than the lightweight markdown
  // parser below can render — e.g. tables, icons, colour-coded tier
  // rows. Those provide a `Component` prop and we render that
  // instead. Article still has `body` for search and the article-list
  // preview snippet (first non-empty line).
  const RichBody = article.Component;
  return (
    <div>
      <button
        onClick={onBack}
        className="text-sm text-slate-500 hover:text-indigo-600 dark:text-slate-400 dark:hover:text-indigo-300 flex items-center gap-1 mb-4"
      >
        <ChevronLeft size={14} /> All articles
      </button>
      <Badge tone="indigo">{article.category}</Badge>
      <h2 className="text-xl font-bold text-slate-900 dark:text-white mt-2 mb-5">{article.title}</h2>
      <div className="text-sm text-slate-700 dark:text-slate-200 leading-relaxed space-y-3">
        {RichBody ? <RichBody /> : lines.map((line, i) => {
          if (/^\d+\.\s/.test(line)) {
            return (
              <div key={i} className="flex gap-3">
                <span className="text-indigo-600 dark:text-indigo-400 font-semibold">{line.match(/^\d+/)?.[0]}.</span>
                <span dangerouslySetInnerHTML={{ __html: renderInline(line.replace(/^\d+\.\s/, '')) }} />
              </div>
            );
          }
          if (/^-\s/.test(line)) {
            return (
              <div key={i} className="flex gap-3 pl-2">
                <span className="text-slate-400 dark:text-slate-500">•</span>
                <span dangerouslySetInnerHTML={{ __html: renderInline(line.replace(/^-\s/, '')) }} />
              </div>
            );
          }
          if (!line.trim()) return <div key={i} className="h-2" />;
          return <p key={i} dangerouslySetInnerHTML={{ __html: renderInline(line) }} />;
        })
      }
      </div>
      <div className="mt-8 p-4 rounded-xl bg-gradient-to-br from-indigo-50 to-violet-50 dark:from-indigo-500/10 dark:to-violet-500/10 border border-indigo-100 dark:border-indigo-500/20">
        <div className="font-semibold text-slate-900 dark:text-white mb-1">Still stuck?</div>
        <div className="text-sm text-slate-600 dark:text-slate-300 mb-3">
          We usually reply within a few business hours.
        </div>
        <a
          href="mailto:support@docketflowapp.com"
          className="text-sm font-semibold text-indigo-700 dark:text-indigo-300 hover:text-indigo-800 inline-flex items-center gap-1"
        >
          support@docketflowapp.com <ExternalLink size={12} />
        </a>
      </div>
    </div>
  );
}

const renderInline = (s) =>
  s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/`(.+?)`/g, '<code class="px-1 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-xs">$1</code>');

/* =====================================================================
 *  4.  Help FAB — always-visible floating button
 * ===================================================================== */

export function HelpFab() {
  const { openHelp, restartTour } = useOnboardingHelpers();
  const [open, setOpen] = useState(false);

  // close on outside click
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (!e.target.closest('[data-help-fab]')) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  return (
    <div data-help-fab className="fixed bottom-20 right-4 lg:bottom-6 lg:right-6 z-40">
      {open && (
        <div className="mb-2 w-56 rounded-xl bg-white dark:bg-slate-900 shadow-2xl border border-slate-200 dark:border-slate-700 overflow-hidden">
          <button
            onClick={() => { setOpen(false); openHelp(); }}
            className="w-full px-4 py-2.5 text-left text-sm hover:bg-slate-50 dark:hover:bg-slate-800 flex items-center gap-2 border-b border-slate-100 dark:border-slate-800 text-slate-700 dark:text-slate-200"
          >
            <BookOpen size={14} className="text-indigo-600 dark:text-indigo-400" /> Browse help docs
          </button>
          <button
            onClick={() => { setOpen(false); restartTour(); }}
            className="w-full px-4 py-2.5 text-left text-sm hover:bg-slate-50 dark:hover:bg-slate-800 flex items-center gap-2 border-b border-slate-100 dark:border-slate-800 text-slate-700 dark:text-slate-200"
          >
            <Play size={14} className="text-indigo-600 dark:text-indigo-400" /> Replay product tour
          </button>
          <a
            href="mailto:support@docketflowapp.com"
            className="w-full px-4 py-2.5 text-left text-sm hover:bg-slate-50 dark:hover:bg-slate-800 flex items-center gap-2 text-slate-700 dark:text-slate-200"
          >
            <MessageCircle size={14} className="text-indigo-600 dark:text-indigo-400" /> Contact support
          </a>
        </div>
      )}
      <button
        onClick={() => setOpen((o) => !o)}
        data-tour="nav-help"
        aria-label="Help"
        className="w-12 h-12 rounded-full bg-indigo-600 hover:bg-indigo-700 text-white shadow-lg flex items-center justify-center transition"
      >
        {open ? <X size={20} /> : <HelpCircle size={20} />}
      </button>
    </div>
  );
}

/* =====================================================================
 *  5.  Onboarding host — renders the modals/overlays at once
 * ===================================================================== */

export function OnboardingHost({ tourSteps }) {
  return (
    <>
      <HelpCenter />
      <GuidedTour steps={tourSteps} />
      <HelpFab />
    </>
  );
}
